import {appendFiltersReadably} from "@/dashboards/filter/FilterParser";
import {count, count as grouseCount} from "@/data/Grouse";
import vuexStore from "@/store/vuex/VuexStore";
import {currentAccountCode} from "@/app/utils/Account";


/**
 * The segment ID for the five star rating scale.
 * @type {number}
 */
export const FIVE_STAR_SEGMENT_ID = 167447;

/**
 * The segment ID for the ten star rating scale.
 * @type {number}
 */
export const TEN_STAR_SEGMENT_ID = 194038;

/**
 * The segment ID for the five option agreement scale.
 * @type {number}
 */
export const FIVE_OPTION_AGREEMENT_SCALE = 196490;

/**
 * The segment ID for the yes/no scale.
 * @type {number}
 */
export const YES_NO_SCALE = 194051;

/**
 * The segment ID for the net promoter score.
 * @type {number}
 */
export const NET_PROMOTER_SCORE_SEGMENT = 194025;

/**
 * The segment ID for the Customer Effort Survey type using five points.
 * @type {number}
 */
export const CES_SURVEY_5POINT_SEGMENT = 194027;

/**
 * The segment ID for the Customer Satisfaction Survey type.
 * @type {number}
 */
export const CSAT_SURVEY_SEGMENT = 194024;

/**
 * The segment ID for the First Contact Resolution survey type.
 * @type {number}
 */
export const FCR_SURVEY_SEGMENT = 194028;

/**
 * The segment ID for the Verbatim survey Question Type
 * @type {number}
 */
export const VERBATIM_SURVEY_SEGMENT = 194068;

export const zeroToTenStarTagIds = [
    { id: 194040, rating: 0 },
    { id: 194041, rating: 1 },
    { id: 194042, rating: 2 },
    { id: 194043, rating: 3 },
    { id: 194044, rating: 4 },
    { id: 194045, rating: 5 },
    { id: 194046, rating: 6 },
    { id: 194047, rating: 7 },
    { id: 194048, rating: 8 },
    { id: 194049, rating: 9 },
    { id: 194050, rating: 10 }
];

export const fiveStarTagIds = [
    { id: 167449, rating: 1},
    {id: 167450, rating: 2},
    {id: 167451, rating: 3},
    {id: 167452, rating: 4},
    {id: 167453, rating: 5}
];

export const fivePointAgreementTagIds = [
    { id: 196496, rating: 0 }, // STRONGLY_DISAGREE
    { id: 196495, rating: 1 }, // DISAGREE
    { id: 196494, rating: 2 }, // NEITHER_AGREE_NOR_DISAGREE
    { id: 196493, rating: 3 }, // AGREE
    { id: 196492, rating: 4 }  // STRONGLY_AGREE
];

export const oneToSevenPointScale = [
    { id: 194031, rating: 0 }, // ONE
    { id: 194032, rating: 1 }, // TWO
    { id: 194033, rating: 2 }, // THREE
    { id: 194034, rating: 3 }, // FOUR
    { id: 194035, rating: 4 }, // FIVE
    { id: 194036, rating: 5 }, // SIX
    { id: 194037, rating: 6 }  // SEVEN
];

export const oneToTenStarTagIds = [
    { id: 259365, rating: "1" },
    { id: 259366, rating: "2" },
    { id: 259367, rating: "3" },
    { id: 259368, rating: "4" },
    { id: 259370, rating: "5" },
    { id: 259371, rating: "6" },
    { id: 259372, rating: "7" },
    { id: 259373, rating: "8" },
    { id: 259374, rating: "9" },
    { id: 259375, rating: "10" }
];

export const googleBusinessFivePointScore = [
    { id: 167449, rating: 0 },
    { id: 167450, rating: 1 },
    { id: 167451, rating: 2 },
    { id: 167452, rating: 3 },
    { id: 167453, rating: 4 }
];

export const helloPeterFivePointScore = [
    { id: 167449, rating: 0 },
    { id: 167450, rating: 1 },
    { id: 167451, rating: 2 },
    { id: 167452, rating: 3 },
    { id: 167453, rating: 4 }
];

export const trustPilotFivePointScore = [
    { id: 167449, rating: 0 },
    { id: 167450, rating: 1 },
    { id: 167451, rating: 2 },
    { id: 167452, rating: 3 },
    { id: 167453, rating: 4 }
];



export function getNetPromoterScoreFilter() {
    return `segment is ${NET_PROMOTER_SCORE_SEGMENT} and segment is ${TEN_STAR_SEGMENT_ID}`;
}

export function getCes5PointScoreFilter() {
    return `segment is ${CES_SURVEY_5POINT_SEGMENT} and segment is ${FIVE_OPTION_AGREEMENT_SCALE}`;
}

export function getCsatScoreFilter() {
    return `segment is ${CSAT_SURVEY_SEGMENT} and segment is ${FIVE_STAR_SEGMENT_ID}`;
}

export function getFcrScoreFilter() {
    return `segment is ${FCR_SURVEY_SEGMENT} and segment is ${YES_NO_SCALE}`;
}

export function getCustomQuestionFilter() {
    const tags = vuexStore.state.tags

    let customQuestionsFilter = tags.filter(it => it.namespace === "triflecustomquestion").map(it => it.id).join(" OR TAG IS ")

    if (customQuestionsFilter.length > 0) customQuestionsFilter = "TAG IS " + customQuestionsFilter;

    return customQuestionsFilter;
}

/**
 * Returns the total number of mentions that have been tagged with a
 * net promoter score segment.
 * @param filter
 * @return {Promise<number>}
 */
export async function getTotalNpsMentions(filter) {
    filter = appendFiltersReadably(filter, getNetPromoterScoreFilter());
    const { mentionCount } = await grouseCount(filter);
    return mentionCount;
}

/**
 * Returns the total number of mentions that have been tagged with a
 * CSat score segment.
 * @param filter
 * @return {Promise<number>}
 */
export async function getTotalCsatMentions(filter) {
    filter = appendFiltersReadably(filter, getCsatScoreFilter());
    const { mentionCount } = await grouseCount(filter);
    return mentionCount;
}

/**
 * Returns the total number of mentions that have been tagged with a
 * CES 5 point score segment.
 * @param filter
 * @return {Promise<number>}
 */
export async function getTotalCes5PointMentions(filter) {
    filter = appendFiltersReadably(filter, getCes5PointScoreFilter());
    const { mentionCount } = await grouseCount(filter);
    return mentionCount;
}

/**
 * Returns the total number of mentions that have been tagged with a
 * FCR segment.
 * @param filter
 * @return {Promise<number>}
 */
export async function getTotalFcrMentions(filter) {
    filter = appendFiltersReadably(filter, getFcrScoreFilter());
    const { mentionCount } = await grouseCount(filter);
    return mentionCount;
}



/**
 * Calculates the net promoter score (as a value between -1 and 1)
 * given the supplied values.
 * @return {number}
 */
export function calculateNpsScoreFrom(totalMentions, numDetractors, numPromoters) {
    if (!Number.isFinite(totalMentions)) throw new Error("totalMentions must be an integer");
    if (!Number.isFinite(numDetractors)) throw new Error("numDetractors must be an integer");
    if (!Number.isFinite(numPromoters)) throw new Error("numPromoters must be an integer");
    if (numDetractors < 0) throw new Error("numDetractors should not be negative");
    if (numPromoters < 0) throw new Error("numPromoters should not be negative");
    if (totalMentions <= 0) return totalMentions;

    const promoterP = numPromoters / totalMentions;
    const detractorP = numDetractors / totalMentions;

    return promoterP - detractorP;
}

/**
 * Given a filter, this pulls data from the account and then calculates
 * the nps score as a value between -1 and 1.
 * @param filter
 * @return {Promise<number>}
 */
export async function calculateNpsScore(filter) {
    const totalFilter = appendFiltersReadably(filter, getNetPromoterScoreFilter());
    const detractorFilter = appendFiltersReadably(filter, getNpsDetractorsFilter());
    const promoterFilter = appendFiltersReadably(filter, getNpsPromotersFilter());

    const [total,
        detractors,
        promoters] = await Promise.all([count(totalFilter), count(detractorFilter), count(promoterFilter)]);

    return calculateNpsScoreFrom(total.mentionCount, detractors.mentionCount, promoters.mentionCount);
}

/**
 * Calculates the CSAT score.
 * @param totalMentions
 * @param sumOfFoursAndFives
 * @return {number}
 */
export function calculateCsatScoreFrom(totalMentions, sumOfFoursAndFives) {
    if (!Number.isFinite(totalMentions)) throw new Error("totalMentions must be an integer");
    if (!Number.isFinite(sumOfFoursAndFives)) throw new Error("sumOfFoursAndFives must be an integer");
    if (sumOfFoursAndFives < 0) throw new Error("sumOfFoursAndFives should not be negative");
    if (totalMentions <= 0) return totalMentions;

    return sumOfFoursAndFives / totalMentions;
}

/**
 * Given a filter, this pulls data from the account and then calculates
 * the CSat score as a value between 0 and 1.
 * @param filter
 * @return {Promise<number>}
 */
export async function calculateCsatScore(filter) {
    const totalFilter = appendFiltersReadably(filter, getCsatScoreFilter());
    const fourAndFiveFilter = appendFiltersReadably(totalFilter, "segment is 167452 or segment is 167453");

    const [total,
        fourAndFive] = await Promise.all([count(totalFilter), count(fourAndFiveFilter)]);

    return calculateCsatScoreFrom(total.mentionCount, fourAndFive.mentionCount);
}

/**
 * Calculates the CES 5 point score using the given inputs.
 * @param {number} totalMentions
 * @param {number} totalAgree
 * @param {number} totalStronglyAgree
 * @return {number}
 */
export function calculateCes5PointScoreFrom(totalMentions, totalAgree, totalStronglyAgree) {
    if (!Number.isFinite(totalMentions)) throw new Error("totalMentions must be an integer");
    if (!Number.isFinite(totalAgree)) throw new Error("totalAgree must be an integer");
    if (!Number.isFinite(totalStronglyAgree)) throw new Error("totalStronglyAgree must be an integer");
    if (totalAgree < 0) throw new Error("totalAgree should not be negative");
    if (totalStronglyAgree < 0) throw new Error("totalStronglyAgree should not be negative");
    if (totalMentions <= 0) return totalMentions;

    return (totalAgree + totalStronglyAgree) / totalMentions;
}

/**
 * Calculates the CES score by querying the API using the given filter.
 * @param filter
 * @return {Promise<number>}
 */
export async function calculateCes5PointScore(filter) {
    const totalFilter = appendFiltersReadably(filter, getCes5PointScoreFilter());
    const stronglyFilter = appendFiltersReadably(totalFilter, "segment is 196492");
    const agreeFilter = appendFiltersReadably(totalFilter, "segment is 196493");

    const [total,
        strongly,
        agree] = await Promise.all([count(totalFilter), count(stronglyFilter), count(agreeFilter)]);

    return calculateCes5PointScoreFrom(total.mentionCount, strongly.mentionCount, agree.mentionCount);
}

/**
 * Calculates the First Contact Resolution score (FCR score) using the given inputs.
 * @param totalMentions
 * @param totalYes
 * @return {number}
 */
export function calculateFcrScoreFrom(totalMentions, totalYes) {
    if (!Number.isFinite(totalMentions)) throw new Error("totalMentions must be an integer");
    if (!Number.isFinite(totalYes)) throw new Error("totalYes must be an integer");
    if (totalYes < 0) throw new Error("totalYes should not be negative");
    if (totalMentions <= 0) return totalMentions;

    return totalYes / totalMentions;
}

/**
 * Calculates the FCR score after querying the API for the specified data.
 * @param filter
 * @return {Promise<number>}
 */
export async function calculateFcrScore(filter) {
    const totalFilter = appendFiltersReadably(filter, getFcrScoreFilter());
    const yesFilter = appendFiltersReadably(totalFilter, "segment is 194053");

    const [total,
        yes] = await Promise.all([count(totalFilter), count(yesFilter)]);

    return calculateFcrScoreFrom(total.mentionCount, yes.mentionCount);
}



/**
 * These segments define the tags for what NPS considers
 * "detractors" (people who scored a service low).
 * @type {number[]}
 */
export const NPS_IDS_DETRACTORS = Object.freeze([
    194040,
    194041,
    194042,
    194043,
    194044,
    194045,
    194046
]);

/**
 * These segments define the tags for what NPS considers
 * "passive" (people who scored a service mediumly).
 * @type {number[]}
 */
export const NPS_IDS_PASSIVES = Object.freeze([
    194047,
    194048
]);

/**
 * These segments define the tags for what NPS considers
 * "promoters" (people who scored a service high).
 * @type {number[]}
 */
export const NPS_IDS_PROMOTERS = Object.freeze([
    194050,
    194049
]);

export const NPS_PROMOTER_DESCRIPTION = "Respondents who gave a score of 9 or 10. They are considered to be satisfied, enthusiastic customers who will promote the brand.";
export const NPS_PASSIVE_DESCRIPTION = "Respondents who gave a score of 7 or 8. They are considered to be satisfied, but not enthusiastic, customers. They may be vulnerable to competitors.";
export const NPS_DETRACTOR_DESCRIPTION = "Respondents who gave a score of 6 or lower. They may be unhappy customers, who can damage the brand and impede growth.";

/**
 * Returns a filter for determining NPS detractors
 * @return {string}
 */
export function getNpsDetractorsFilter() {
    return NPS_IDS_DETRACTORS.map(val => `segment is ${val}`).join(' or ');
}


/**
 * Returns a filter for determining NPS passives
 * @return {string}
 */
export function getNpsPassivesFilter() {
    return NPS_IDS_PASSIVES.map(val => `segment is ${val}`).join(' or ');
}

/**
 * Returns a filter for determining NPS promoters
 * @return {string}
 */
export function getNpsPromotersFilter() {
    return NPS_IDS_PROMOTERS.map(val => `segment is ${val}`).join(' or ');
}

export function getGroupKey(value) {
    if (!value) throw new Error("Value can not be null")
    if (typeof value === 'object') {
        if ('id' in value) {
            return value.id;
        }
        return JSON.stringify(value);
    }
    return value;
}

/**
 * Calculates an average of 5-star survey responses. If a groupby key is present, it returns the result as such, where it is an average grouped by eg socialNetwork
 * @param data
 * @param groupKey
 * @param label
 * @returns {{[]: number}|*[]}
 */
export function getAverageCsatScoreFrom(data, groupKey, label = "averageCsatRating"){
    const result = [];

    if (!groupKey) {
        const totalRatings = data.map((val, index) => val.mentionCount * (index + 1)).reduce((acc, num) => acc + num, 0);
        const totalMentions = data.map((val, index) => val.mentionCount).reduce((acc, num) => acc + num, 0);
        const ave = totalRatings/totalMentions;
        return {[label]: !Number.isNaN(ave) ? ave : 0};
    }

    const map = new Map()
    for (const [index, entry] of data.entries()) {
        for (const inner of entry){
            if (!map.has(getGroupKey(inner[groupKey]))){
                map.set(getGroupKey(inner[groupKey]), {
                    obj: inner[groupKey],
                    totalRatings: inner.mentionCount * (index + 1),
                    totalMentions: inner.mentionCount
                })
            } else {
                map.set(getGroupKey(inner[groupKey]), {
                    ...map.get(getGroupKey(inner[groupKey])),
                    totalRatings: map.get(getGroupKey(inner[groupKey])).totalRatings + inner.mentionCount * (index + 1),
                    totalMentions: map.get(getGroupKey(inner[groupKey])).totalMentions + inner.mentionCount

                })
            }
        }
    }
    // calculate averages
    for (const [key, value] of map) {
        const ave = value.totalRatings / value.totalMentions;
        result.push({
            [groupKey]: value.obj,
            [label]: !Number.isNaN(ave) ? ave : 0
        })
    }
    return result;
}

/**
 * Calculates an average of 10-star survey responses. If a groupby key is present, it returns the result as such, where it is an average grouped by eg socialNetwork
 * @param data
 * @param groupKey
 * @returns {*[]|{averageNpsRating: number}}
 */
export function getAverageNpsScoreFrom(data, groupKey){
    const result = [];
    if (!groupKey) {
        const totalRatings = data.map((val, index) => val.mentionCount * index).reduce((acc, num) => acc + num, 0);
        const totalMentions = data.map((val, index) => val.mentionCount).reduce((acc, num) => acc + num, 0);
        const ave = totalRatings/totalMentions;
        return {averageNpsRating: !Number.isNaN(ave) ? ave : 0};
    }

    const map = new Map()
    for (const [index, entry] of data.entries()) {
        for (const inner of entry){
            if (!map.has(getGroupKey(inner[groupKey]))){
                map.set(getGroupKey(inner[groupKey]), {
                    obj: inner[groupKey],
                    totalRatings: inner.mentionCount * index,
                    totalMentions: inner.mentionCount
                })
            } else {
                map.set(getGroupKey(inner[groupKey]), {
                    ...map.get(getGroupKey(inner[groupKey])),
                    totalRatings: map.get(getGroupKey(inner[groupKey])).totalRatings + inner.mentionCount * index,
                    totalMentions: map.get(getGroupKey(inner[groupKey])).totalMentions + inner.mentionCount

                })
            }
        }
    }
    // calculate averages
    for (const [key, value] of map) {
        const ave = value.totalRatings / value.totalMentions;
        result.push({
            [groupKey]: value.obj,
            averageNpsRating: !Number.isNaN(ave) ? ave : 0
        })
    }
    return result;
}

/**
 * Given a filter, gets the average nps 10-star score from the survey mentions that match that filter
 * @param filter
 * @returns {Promise<number>}
 */
export async function getAverageNpsScore(filter) {
    const data = await Promise.all(
        zeroToTenStarTagIds
            .map(it => count(appendFiltersReadably(filter, appendFiltersReadably(`TAG IS ${it.id}`, getNetPromoterScoreFilter()))))
    );

    return getAverageNpsScoreFrom(data, null).averageNpsRating;
}

/**
 * Given a filter, gets the average csat 5-star score from the survey mentions that match that filter
 * @param filter
 * @returns {Promise<number>}
 */
export async function getAverageCsatScore(filter) {
    const data = await Promise.all(
        fiveStarTagIds
            .map(it => count(appendFiltersReadably(filter, appendFiltersReadably(`TAG IS ${it.id}`, getCsatScoreFilter()))))
    );

    return getAverageCsatScoreFrom(data, null).averageCsatRating;
}

/**
 * Given a filter, gets the average custom question 10-star score from the survey mentions that match that filter
 * @param filter
 * @returns {Promise<number>}
 */
export async function getAverageCustomScore(filter) {
    const data = await Promise.all(
        oneToTenStarTagIds
            .map(it => count( appendFiltersReadably(filter, `TAG IS ${it.id}`)))
    );

    return getAverageCsatScoreFrom(data, null, "averageCustomQuestionScore").averageCustomQuestionScore;
}

/**
 * Given a filter, gets the average ces 5 score from the survey mentions that match that filter
 * @param filter
 * @returns {Promise<number>}
 */
export async function getAverageFivePointCesScore(filter) {
    const data = await Promise.all(
        fivePointAgreementTagIds
            .map(it => count(
                appendFiltersReadably(filter, appendFiltersReadably(`TAG IS ${it.id}`, `TAG IS 196490`))
            ))
    );

    return getAverageCsatScoreFrom(data, null, "averageFivePointCesScore").averageFivePointCesScore;
}

/**
 * Given a filter, gets the average ces 7 point score from the survey mentions that match that filter
 * @param filter
 * @returns {Promise<number>}
 */
export async function getAverageSevenPointCesScore(filter) {
    const data = await Promise.all(
        oneToSevenPointScale
            .map(it => count(
                appendFiltersReadably(filter, appendFiltersReadably(`TAG IS ${it.id}`, `TAG IS 194029`))
            ))
    );

    return getAverageCsatScoreFrom(data, null, "averageSevenPointCesScore").averageSevenPointCesScore;
}

export async function getAverageFivePointGoogleBusinessScore(filter) {
    const data = await Promise.all(
        googleBusinessFivePointScore
            .map(
                it => count(
                    appendFiltersReadably(filter, appendFiltersReadably(`TAG IS ${it.id}`, `socialNetwork IS GOOGLEBUSINESS`))
                )
            )
    );
    return getAverageCsatScoreFrom(data, null, "averageFivePointGoogleBusinessScore").averageFivePointGoogleBusinessScore;
}

export async function getAverageFivePointHelloPeterScore(filter) {
    const data = await Promise.all(
        helloPeterFivePointScore
            .map(
                it => count(
                    appendFiltersReadably(filter, appendFiltersReadably(`TAG IS ${it.id}`, `socialNetwork IS HELLOPETER`))
                )
            )
    );
    return getAverageCsatScoreFrom(data, null, "averageFivePointHelloPeterScore").averageFivePointHelloPeterScore;
}

export async function getAverageFivePointTrustPilotScore(filter) {
    const data = await Promise.all(
        trustPilotFivePointScore
            .map(
                it => count(
                    appendFiltersReadably(filter, appendFiltersReadably(`TAG IS ${it.id}`, `socialNetwork IS TRUSTPILOT`))
                )
            )
    );
    return getAverageCsatScoreFrom(data, null, "averageFivePointTrustPilotScore").averageFivePointTrustPilotScore;
}